import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { EmptyState, ErrorBanner, Spinner, Toast } from '../components/ui';
import { api, type Account } from '../lib/api';

/**
 * Account administration and the OAuth entry point.
 *
 * The refresh-token countdown is the important number on this page: OLX refresh
 * tokens expire 30 days after last use, and the only recovery is a human
 * repeating the browser consent flow.
 */
export function AccountsPage() {
  const queryClient = useQueryClient();
  const [notice, setNotice] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);

  const accounts = useQuery({ queryKey: ['accounts'], queryFn: api.accounts.list });
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['accounts'] });

  const syncCategories = useMutation({
    mutationFn: api.categories.sync,
    onSuccess: (result) => setNotice(`Category tree synced — ${result.count} categories cached.`),
  });

  const resolveLimits = useMutation({
    mutationFn: api.limits.resolve,
    onSuccess: (result) =>
      setNotice(
        `Limits resolved — ${result.rulesUpserted} rules, ${result.scopesCreated} category scopes` +
          (result.unresolved.length > 0 ? `, ${result.unresolved.length} need manual mapping.` : '.'),
      ),
  });

  const testTelegram = useMutation({
    mutationFn: api.telegram.test,
    onSuccess: (result) =>
      setNotice(result.messageId ? 'Test message delivered to the Telegram group.' : 'Telegram is not configured.'),
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Accounts</h1>
          <p className="mt-1 text-sm text-slate-400">OLX seller accounts, their OAuth state and publishing defaults.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" className="btn-ghost" onClick={() => syncCategories.mutate()} disabled={syncCategories.isPending}>
            {syncCategories.isPending ? <Spinner /> : null}
            Sync categories
          </button>
          <button type="button" className="btn-ghost" onClick={() => resolveLimits.mutate()} disabled={resolveLimits.isPending}>
            {resolveLimits.isPending ? <Spinner /> : null}
            Resolve limits
          </button>
          <button type="button" className="btn-ghost" onClick={() => testTelegram.mutate()} disabled={testTelegram.isPending}>
            Test Telegram
          </button>
          <button type="button" className="btn-primary" onClick={() => setShowForm((value) => !value)}>
            {showForm ? 'Cancel' : 'Add account'}
          </button>
        </div>
      </div>

      {notice ? <Toast tone="success">{notice}</Toast> : null}
      {syncCategories.isError ? <ErrorBanner error={syncCategories.error} /> : null}
      {resolveLimits.isError ? <ErrorBanner error={resolveLimits.error} /> : null}

      {showForm ? (
        <NewAccountForm
          onCreated={() => {
            setShowForm(false);
            setNotice('Account created. Click Connect to run the OAuth flow.');
            void invalidate();
          }}
        />
      ) : null}

      {accounts.isLoading ? (
        <div className="card flex justify-center py-16">
          <Spinner className="h-6 w-6 text-slate-500" />
        </div>
      ) : accounts.data && accounts.data.length > 0 ? (
        <div className="grid gap-4 lg:grid-cols-2">
          {accounts.data.map((account) => (
            <AccountCard key={account.id} account={account} onChanged={invalidate} onNotice={setNotice} />
          ))}
        </div>
      ) : (
        <div className="card">
          <EmptyState
            title="No accounts yet"
            hint="Add one here, or seed them through OLX_ACCOUNTS_JSON in the backend environment."
          />
        </div>
      )}
    </div>
  );
}

function AccountCard({
  account,
  onChanged,
  onNotice,
}: {
  account: Account;
  onChanged: () => void;
  onNotice: (message: string) => void;
}) {
  const [error, setError] = useState<unknown>(null);

  const connect = useMutation({
    mutationFn: () => api.accounts.authorizeUrl(account.id),
    // Full navigation, not a popup: OLX sets cookies on its own domain and some
    // browsers block third-party storage inside popups.
    onSuccess: (url) => window.location.assign(url),
    onError: setError,
  });

  const refresh = useMutation({
    mutationFn: () => api.accounts.refresh(account.id),
    onSuccess: () => {
      onNotice(`Token refreshed for "${account.name}".`);
      onChanged();
    },
    onError: setError,
  });

  const verify = useMutation({
    mutationFn: () => api.accounts.verify(account.id),
    onSuccess: (result) => onNotice(`"${account.name}" verified against OLX (user id ${result.olxUserId}).`),
    onError: setError,
  });

  const refreshDaysLeft = account.refreshExpiresAt
    ? Math.round((new Date(account.refreshExpiresAt).getTime() - Date.now()) / 86_400_000)
    : null;

  return (
    <div className="card p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-semibold">{account.label ?? account.name}</h2>
          <p className="mt-0.5 font-mono text-xs text-slate-500">
            {account.name} · client {account.clientId}
          </p>
        </div>
        <span
          className={
            account.authorized
              ? 'badge border border-emerald-900/60 bg-emerald-950/50 text-emerald-300'
              : 'badge border border-red-900/60 bg-red-950/50 text-red-300'
          }
        >
          {account.authorized ? 'authorized' : 'not authorized'}
        </span>
      </div>

      <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
        <Detail label="Advertiser" value={account.advertiserType} />
        <Detail label="Adverts tracked" value={String(account.publishedCount)} />
        <Detail label="City / district" value={`${account.defaultCityId}${account.defaultDistrictId ? ` / ${account.defaultDistrictId}` : ''}`} />
        <Detail label="Contact" value={account.contactName} />
        <Detail
          label="Access token"
          value={
            account.tokenExpiresAt
              ? `${Math.max(Math.round((new Date(account.tokenExpiresAt).getTime() - Date.now()) / 3_600_000), 0)}h left`
              : '—'
          }
        />
        <Detail
          label="Refresh token"
          value={refreshDaysLeft === null ? '—' : `${refreshDaysLeft}d left`}
          tone={refreshDaysLeft !== null && refreshDaysLeft < 7 ? 'warn' : undefined}
        />
      </dl>

      {account.refreshFailures > 0 ? (
        <p className="mt-3 text-xs text-amber-300">
          {account.refreshFailures} consecutive refresh failures
          {account.lastError ? `: ${account.lastError}` : ''}
        </p>
      ) : null}

      {error ? (
        <div className="mt-3">
          <ErrorBanner error={error} />
        </div>
      ) : null}

      <div className="mt-4 flex flex-wrap gap-2">
        <button type="button" className="btn-primary px-3 py-1.5 text-xs" onClick={() => connect.mutate()} disabled={connect.isPending}>
          {connect.isPending ? <Spinner /> : null}
          {account.authorized ? 'Re-connect' : 'Connect'}
        </button>
        <button
          type="button"
          className="btn-ghost px-3 py-1.5 text-xs"
          onClick={() => refresh.mutate()}
          disabled={!account.authorized || refresh.isPending}
        >
          Refresh token
        </button>
        <button
          type="button"
          className="btn-ghost px-3 py-1.5 text-xs"
          onClick={() => verify.mutate()}
          disabled={!account.authorized || verify.isPending}
        >
          Verify
        </button>
      </div>
    </div>
  );
}

function Detail({ label, value, tone }: { label: string; value: string; tone?: 'warn' }) {
  return (
    <div>
      <dt className="text-xs text-slate-500">{label}</dt>
      <dd className={tone === 'warn' ? 'text-amber-300' : 'text-slate-200'}>{value}</dd>
    </div>
  );
}

function NewAccountForm({ onCreated }: { onCreated: () => void }) {
  const [form, setForm] = useState({
    name: '',
    label: '',
    clientId: '',
    clientSecret: '',
    redirectUri: `${window.location.origin}/api/auth/olx/callback`,
    contactName: '',
    contactPhone: '',
    defaultCityId: '',
    defaultDistrictId: '',
    advertiserType: 'private',
    defaultCurrency: 'EUR',
  });

  const create = useMutation({
    mutationFn: () =>
      api.accounts.create({
        name: form.name,
        ...(form.label ? { label: form.label } : {}),
        clientId: form.clientId,
        clientSecret: form.clientSecret,
        redirectUri: form.redirectUri,
        contactName: form.contactName,
        ...(form.contactPhone ? { contactPhone: form.contactPhone } : {}),
        defaultCityId: Number(form.defaultCityId),
        ...(form.defaultDistrictId ? { defaultDistrictId: Number(form.defaultDistrictId) } : {}),
        advertiserType: form.advertiserType,
        defaultCurrency: form.defaultCurrency,
      }),
    onSuccess: onCreated,
  });

  const set = (key: keyof typeof form) => (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm((previous) => ({ ...previous, [key]: event.target.value }));

  return (
    <form
      className="card space-y-4 p-6"
      onSubmit={(event) => {
        event.preventDefault();
        create.mutate();
      }}
    >
      <h2 className="font-semibold">New OLX account</h2>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Input label="Key (used in Telegram)" value={form.name} onChange={set('name')} required placeholder="Eurostoki" />
        <Input label="Display label" value={form.label} onChange={set('label')} placeholder="Eurostoki — main" />
        <Input label="OLX client id" value={form.clientId} onChange={set('clientId')} required placeholder="200238" />
        <Input
          label="OLX client secret"
          value={form.clientSecret}
          onChange={set('clientSecret')}
          required
          type="password"
          hint="Encrypted with AES-256-GCM before storage."
        />
        <Input label="Redirect URI" value={form.redirectUri} onChange={set('redirectUri')} required className="lg:col-span-2" />
        <Input label="Contact name" value={form.contactName} onChange={set('contactName')} required placeholder="Артур" />
        <Input label="Contact phone" value={form.contactPhone} onChange={set('contactPhone')} placeholder="+359…" />
        <Input label="Default city id" value={form.defaultCityId} onChange={set('defaultCityId')} required placeholder="1295" />
        <Input label="Default district id" value={form.defaultDistrictId} onChange={set('defaultDistrictId')} placeholder="719" />

        <div>
          <label className="label">Advertiser type</label>
          <select className="input" value={form.advertiserType} onChange={set('advertiserType')}>
            <option value="private">private (частни обяви)</option>
            <option value="business">business</option>
          </select>
        </div>

        <Input label="Currency" value={form.defaultCurrency} onChange={set('defaultCurrency')} />
      </div>

      {create.isError ? <ErrorBanner error={create.error} /> : null}

      <button type="submit" className="btn-primary" disabled={create.isPending}>
        {create.isPending ? <Spinner /> : null}
        Create account
      </button>
    </form>
  );
}

function Input({
  label,
  hint,
  className,
  ...props
}: React.InputHTMLAttributes<HTMLInputElement> & { label: string; hint?: string }) {
  return (
    <div className={className}>
      <label className="label">{label}</label>
      <input className="input" {...props} />
      {hint ? <p className="mt-1 text-xs text-slate-500">{hint}</p> : null}
    </div>
  );
}
